import { filter, groupBy } from 'lodash';
import { beginTask, endTask } from 'redux-nprogress';
import { selectQuery } from '../../../../store/reducers/location.selectors';
import { redirect } from 'redux-first-router';
import { RENEW_SUBSCRIPTION_VIEW } from '../../PaymentInformation/module';
import { GO_LOGIN } from '../../../../store/actions/auth.types';
import routes from '../../../routes.global';
import viewNames from '../../../../store/actions/view.types';
import { loadToolDescriptions } from '../../../../store/reducers/toolDescriptions';

// ------------------------------------
// Selectors
// ------------------------------------
export const STORE_KEY = 'huddleSearch';
export const selectHuddleSearchState = state => state[STORE_KEY];
export const selectHuddleResults = state => selectHuddleSearchState(state).results;
export const selectSelectedPlayers = state => selectHuddleSearchState(state).selectedPlayers;

// ------------------------------------
// Reducer
// ------------------------------------
const actions = {};
const initialState = {
  activeSearchFilter: 'active',
  pagination: {},
  activePlayerFilter: '',
  activePositionFilter: '',
  activeSearchQuery: '',
  results: {},
  lastPageResults: {},
  selectedPlayers: [],
  hasLoaded: false,
  error: null,
};

const huddleSearchReducer = (state = initialState, action) => {
  const handler = actions[action.type];

  return handler ? handler(state, action) : state;
};

export default huddleSearchReducer;

// ------------------------------------
// Load Huddle Search View Data
// ------------------------------------
export const LOAD_HUDDLE_SEARCH_REQUEST = `${STORE_KEY}/LOAD_HUDDLE_SEARCH_REQUEST`;
export const LOAD_HUDDLE_SEARCH_SUCCESS = `${STORE_KEY}/LOAD_HUDDLE_SEARCH_SUCCESS`;
export const LOAD_HUDDLE_LAST_PAGE_RESULTS = `${STORE_KEY}/LOAD_HUDDLE_LAST_PAGE_RESULTS`;
export const LOAD_HUDDLE_SEARCH_ERROR = `${STORE_KEY}/LOAD_HUDDLE_SEARCH_ERROR`;

export const loadHuddleSearchRequest = ({ search, position, searchBy, player }) => ({
  type: LOAD_HUDDLE_SEARCH_REQUEST,
  payload: { search, position, searchBy, player },
});
export const loadHuddleSearchSuccess = (results, pagination) => ({
  type: LOAD_HUDDLE_SEARCH_SUCCESS,
  payload: { results, pagination },
});
export const loadHuddleSearchLastPageResults = (lastPageResults) => ({
  type: LOAD_HUDDLE_LAST_PAGE_RESULTS,
  payload: { lastPageResults },
});
export const loadHuddleSearchError = error => ({
  type: LOAD_HUDDLE_SEARCH_ERROR,
  payload: { error },
});

actions[LOAD_HUDDLE_SEARCH_REQUEST] = (
  state,
  { payload: { position, search, searchBy, player } },
) => ({ ...state,
  activePlayerFilter: player || '',
  activePositionFilter: position || '',
  activeSearchFilter: searchBy,
  activeSearchQuery: search,
  selectedPlayers: [],
  results: {},
  hasLoaded: false,
});
actions[LOAD_HUDDLE_SEARCH_SUCCESS] = (state, { payload: { results, pagination } }) => ({ ...state,
  results: groupBy(results, 'position'),
  pagination,
  hasLoaded: true,
});
actions[LOAD_HUDDLE_LAST_PAGE_RESULTS] = (state, { payload: { lastPageResults } }) => ({...state,
  lastPageResults,
});
actions[LOAD_HUDDLE_SEARCH_ERROR] = (state, { payload: { error } }) => ({ ...state,
  error,
});

export const loadHuddleSearch = ({
  position,
  search,
  searchBy,
  player,
  page,
}) => async (dispatch, getState, { api }) => {
  try {
    dispatch(beginTask());
    dispatch(loadHuddleSearchRequest({ position, search, searchBy, player }));
    if (position) {
      let { search_results: results, pagination } = await api.players.playerSearchByPosition({ position, page, pageSize: 20 });
      pagination = { numPages: pagination.number_of_pages, currentPage: pagination.page_number };
      dispatch(loadHuddleSearchSuccess(results, pagination));
      if (pagination) {
        const { search_results } = await api.players.playerSearchByPosition({ position, page: pagination.numPages, pageSize: 20 });
        const lastPageResults = search_results.length;
        dispatch(loadHuddleSearchLastPageResults(lastPageResults));
      }
    } else if (player || (player && search)) {
      const { search_results: players } = await api.players.playerSearchByName({
        name: player.split(' ')[player.split(' ').length - 1],
        active: searchBy === 'active',
      });

      const results = filter(players, ({ fname, lname }) => `${fname} ${lname}` === player);

      dispatch(loadHuddleSearchSuccess(results));
    } else if (search && !player) {
      const { search_results: results } = await api.players.playerSearchByName({
        name: search,
        active: searchBy === 'active',
      });

      dispatch(loadHuddleSearchSuccess(results));
    } else {
      dispatch(loadHuddleSearchSuccess([]));
    }
  } catch (error) {
    dispatch(loadHuddleSearchError(error));
  } finally {
    dispatch(endTask());
  }
};

// ------------------------------------
// Route / View
// ------------------------------------
export const HUDDLE_SEARCH_VIEW = `${STORE_KEY}/HUDDLE_SEARCH_VIEW`;
export const PLAYER_LANDING_VIEW = `${STORE_KEY}/PLAYER_LANDING_VIEW`;
export const goHuddleSearchView = () => ({ type: HUDDLE_SEARCH_VIEW });
export const goPlayerLandingView = () => ({ type: PLAYER_LANDING_VIEW });

viewNames[HUDDLE_SEARCH_VIEW] = 'myhuddle/HuddleSearch';
viewNames[PLAYER_LANDING_VIEW] = 'PlayerLanding';
routes[HUDDLE_SEARCH_VIEW] = {
  path: '/myhuddle/nfl-player-search',
  thunk: async (dispatch, getState) => {
    const { position, search, searchBy, player, page = 1 } = selectQuery(getState());
    const { loggedIn } = getState().auth;
    if (!loggedIn) dispatch(redirect({ type: GO_LOGIN }));
    else {
      const { status } = getState().auth.user;
      if (status === 'C') dispatch(redirect({ type: RENEW_SUBSCRIPTION_VIEW }));
    // Needs to await otherwise data won't load on SSR
      else {
        await dispatch(loadHuddleSearch({
          position,
          search,
          searchBy: searchBy || 'active',
          player,
          page,
        }));
        await dispatch(loadToolDescriptions());
      }
    }
  },
};

routes[PLAYER_LANDING_VIEW] = {
  path: '/playerLanding',
    thunk: async (dispatch, getState) => {
    const { position, search, searchBy, player, page = 1 } = selectQuery(getState());
    const { loggedIn } = getState().auth;
    const { status } = getState().auth.user;
    await dispatch(loadHuddleSearch({
      position,
      search,
      searchBy: searchBy || 'active',
      player,
      page,
    }));
    await dispatch(loadToolDescriptions());
  },
};



// WEBPACK FOOTER //
// ./src/app/views/HuddleSearch/module/huddleSearch.js